import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  createChart,
  ColorType,
  IChartApi,
  ISeriesApi,
  CandlestickData,
  HistogramData,
  Time,
  LineStyle,
  LineData,
  CandlestickSeries,
  LineSeries,
  HistogramSeries,
  createSeriesMarkers,
  SeriesMarker,
} from "lightweight-charts";
import "./AdvancedTradingChart.scss";
import config from "../../../config/config";
import { get } from "../../../services/smartApiService";
import {
  detectChartPatterns,
  validateCandleQuality,
  timeframeToSecs,
  DetectedPattern,
  CandleQuality,
  PatternCandle,
  PATTERN_LABEL,
  PATTERN_FULL_NAME,
  PATTERN_EXPLANATION,
} from "../../../utils/chartPatternDetector";

interface SupportResistanceLevel {
  level: number;
  type: string;
  strength: string;
  reason: string;
}

interface OILevel {
  level: number;
  type: 'oi_support' | 'oi_resistance' | 'max_pain';
  label: string;
  oi?: number;
}

interface SignalMarker {
  type: 'CE' | 'PE';
  score: number;
  timestamp: number;
  symbol: string;
  date: string;
}

interface AdvancedTradingChartProps {
  symbol?: string;
  companyId?: number;
  height?: number;
  wsEnabled?: boolean;
  livePrice?: number;
  onTimeframeChange?: (timeframe: TimeFrame) => void;
  supportLevels?: SupportResistanceLevel[];
  resistanceLevels?: SupportResistanceLevel[];
  oiLevels?: OILevel[];
  showVWAP?: boolean;
  showVolume?: boolean;
  signalMarkers?: SignalMarker[];
  onPatternsDetected?: (patterns: DetectedPattern[]) => void;
}

interface HistoricalCandle {
  timestamp: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

interface ChartData {
  time: Time;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

interface Indicator {
  id: string;
  name: string;
  enabled: boolean;
  color: string;
}

type TimeFrame =
  | "1m"
  | "5m"
  | "15m"
  | "30m"
  | "1h"
  | "4h"
  | "1D"
  | "1W"
  | "1M";

const AdvancedTradingChart: React.FC<AdvancedTradingChartProps> = ({
  symbol = "NIFTY 50",
  companyId,
  height = 600,
  wsEnabled = false,
  livePrice = 0,
  onPatternsDetected,
  onTimeframeChange,
  supportLevels = [],
  resistanceLevels = [],
  oiLevels = [],
  showVWAP = true,
  showVolume = true,
  signalMarkers = [],
}) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);

  const candleRef = useRef<ISeriesApi<"Candlestick"> | null>(null);
  const volumeRef = useRef<ISeriesApi<"Histogram"> | null>(null);
  const markersApiRef = useRef<{ setMarkers: (markers: SeriesMarker<Time>[]) => void } | null>(null);
  const indicatorSeriesRef = useRef<Map<string, ISeriesApi<"Line">>>(
    new Map()
  );

  const [timeFrame, setTimeFrame] = useState<TimeFrame>("15m");
  const [chartData, setChartData] = useState<ChartData[]>([]);
  const [currentPrice, setCurrentPrice] = useState(0);
  const [priceChange, setPriceChange] = useState(0);
  const [priceChangePercent, setPriceChangePercent] = useState(0);
  const [loadingHistorical, setLoadingHistorical] = useState(false);
  const historicalLoadedRef = useRef(false);
  const isChartMountedRef = useRef(false);
  const previousTimeframeRef = useRef<TimeFrame>(timeFrame);
  const previousSymbolRef = useRef<string>(symbol);
  const previousWsEnabledRef = useRef<boolean>(wsEnabled);

  const [showGrid, setShowGrid] = useState(true);
  const [showCrosshair, setShowCrosshair] = useState(true);
  const supportLinesRef = useRef<ISeriesApi<"Line">[]>([]);
  const resistanceLinesRef = useRef<ISeriesApi<"Line">[]>([]);
  const oiLinesRef = useRef<ISeriesApi<"Line">[]>([]);
  const vwapSeriesRef = useRef<ISeriesApi<"Line"> | null>(null);
  const pdhlLinesRef = useRef<ReturnType<ISeriesApi<"Candlestick">['createPriceLine']>[]>([]);
  // Mirror of chartData in a ref so live-price effect doesn't need chartData as a dep
  const chartDataRef = useRef<ChartData[]>([]);

  // Pattern detection state
  const [detectedPatterns, setDetectedPatterns] = useState<DetectedPattern[]>([]);
  const [candleQuality, setCandleQuality] = useState<CandleQuality | null>(null);
  // Tracks which symbol+timeframe patterns have already been computed (avoids re-running on every WS tick)
  const patternsKeyRef = useRef('');
  // Mirror of detectedPatterns so the crosshair handler (subscribed once at chart creation) reads live data
  const detectedPatternsRef = useRef<DetectedPattern[]>([]);
  useEffect(() => { detectedPatternsRef.current = detectedPatterns; }, [detectedPatterns]);

  // Crosshair tooltip state
  const [tooltipData, setTooltipData] = useState<{
    time: string;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number;
    pattern: DetectedPattern | null;
  } | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  const [indicators, setIndicators] = useState<Indicator[]>([
    { id: "sma20", name: "SMA 20", enabled: true, color: "#2196F3" },
    { id: "sma50", name: "SMA 50", enabled: true, color: "#FF9800" },
    { id: "ema9", name: "EMA 9", enabled: false, color: "#4CAF50" },
  ]);

  // Live candle tracking
  const currentCandleRef = useRef<{
    time: Time;
    open: number;
    high: number;
    low: number;
    close: number;
    startTime: number;
  } | null>(null);

  /* -------------------- CHART INIT -------------------- */
  useEffect(() => {
    if (!chartContainerRef.current) return;

    const chart = createChart(chartContainerRef.current, {
      height,
      layout: {
        background: { type: ColorType.Solid, color: "#131722" },
        textColor: "#d1d4dc",
      },
      grid: {
        vertLines: { color: "#1e222d", visible: showGrid },
        horzLines: { color: "#1e222d", visible: showGrid },
      },
      crosshair: {
        mode: 1,
        vertLine: { visible: showCrosshair, style: LineStyle.Dashed },
        horzLine: { visible: showCrosshair, style: LineStyle.Dashed },
      },
      timeScale: {
        timeVisible: true,
        secondsVisible: false,
        borderColor: "#2b2b43",
        rightOffset: 12, // Always show space for future candles (5 days worth)
        barSpacing: 6,
        minBarSpacing: 0.5,
        fixLeftEdge: false, // Allow scrolling left
        fixRightEdge: false, // Allow scrolling right
        lockVisibleTimeRangeOnResize: false, // Don't lock when resizing
        rightBarStaysOnScroll: false, // Allow free scrolling
        tickMarkFormatter: (time: number) => {
          // Convert UTC unix seconds → IST (UTC+5:30) using direct offset — no browser locale dependency
          const IST_MS = (time + 5 * 3600 + 30 * 60) * 1000;
          const d = new Date(IST_MS);
          const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
          const day = d.getUTCDate().toString().padStart(2, '0');
          const mon = months[d.getUTCMonth()];
          const hr = d.getUTCHours().toString().padStart(2, '0');
          const min = d.getUTCMinutes().toString().padStart(2, '0');
          return `${day} ${mon} ${hr}:${min}`;
        },
      },
    });

    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: "#26a69a",
      downColor: "#ef5350",
      wickUpColor: "#26a69a",
      wickDownColor: "#ef5350",
      borderVisible: false,
    });

    const volumeSeries = chart.addSeries(HistogramSeries, {
      priceFormat: { type: "volume" },
      priceScaleId: "volume",
    });

    chart.priceScale("volume").applyOptions(
      showVolume
        ? { scaleMargins: { top: 0.8, bottom: 0 }, visible: true }
        : { scaleMargins: { top: 1, bottom: 0 }, visible: false }
    );

    chartRef.current = chart;
    candleRef.current = candleSeries;
    volumeRef.current = volumeSeries;
    markersApiRef.current = createSeriesMarkers(candleSeries, []);
    isChartMountedRef.current = true;

    // Subscribe to crosshair move events
    chart.subscribeCrosshairMove((param) => {
      if (!param.time || !param.point) {
        setTooltipData(null);
        return;
      }

      const data = param.seriesData.get(candleSeries) as CandlestickData | undefined;
      const volumeData = param.seriesData.get(volumeSeries) as HistogramData | undefined;

      if (data) {
        const timestamp = param.time as number;
        // Convert UTC unix → IST using direct offset
        const istMs = (timestamp + 5 * 3600 + 30 * 60) * 1000;
        const d = new Date(istMs);
        const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        const timeString = `${d.getUTCDate()} ${months[d.getUTCMonth()]} ${d.getUTCFullYear()}, ${d.getUTCHours().toString().padStart(2,'0')}:${d.getUTCMinutes().toString().padStart(2,'0')} IST`;

        // If the hovered candle is where a detected pattern completed, surface its
        // explanation in the tooltip too — this is the only reliable way to attach
        // "what this means" text to a marker, since chart markers only show a short label
        const pattern = detectedPatternsRef.current.find(p => p.completionTime === timestamp) || null;

        setTooltipData({
          time: timeString,
          open: data.open,
          high: data.high,
          low: data.low,
          close: data.close,
          volume: volumeData?.value || 0,
          pattern,
        });

        setTooltipPosition({
          x: param.point.x,
          y: param.point.y,
        });
      }
    });

    const resize = () =>
      chart.applyOptions({
        width: chartContainerRef.current!.clientWidth,
      });

    window.addEventListener("resize", resize);
    return () => {
      isChartMountedRef.current = false;
      window.removeEventListener("resize", resize);
      chart.remove();
      chartRef.current = null;
      candleRef.current = null;
      volumeRef.current = null;
      markersApiRef.current = null;
    };
  }, [height, showGrid, showCrosshair, showVolume]);

  /* -------------------- FETCH HISTORICAL DATA -------------------- */
  const fetchHistoricalData = useCallback(async () => {
    if (!symbol || historicalLoadedRef.current || !isChartMountedRef.current) {
      return;
    }

    try {
      setLoadingHistorical(true);

      // Map timeframe to API format (must match backend timeframe_model_map)
      const timeframeMap: Record<TimeFrame, string> = {
        "1m": "1min",
        "5m": "5min",
        "15m": "15min",
        "30m": "30min",
        "1h": "1hour",
        "4h": "4hour",
        "1D": "1day",
        "1W": "1day",
        "1M": "1day",
      };

      const apiTimeframe = timeframeMap[timeFrame] || "1day";

      // Calculate limit based on timeframe to get ~30 days of data
      const limitMap: Record<string, number> = {
        "5min": 500,    // 30 days * 75 candles/day (9:15-3:30 = 6.25 hours = 75 5-min candles)
        "15min": 500,   // 30 days * 25 candles/day
        "30min": 375,   // 30 days * 12-13 candles/day
        "1hour": 200,   // 30 days * 6-7 candles/day
        "4hour": 60,    // 30 days * 2 candles/day
        "1day": 30,     // 30 days
      };
      const limit = limitMap[apiTimeframe] || 30;

      const response: any = await get(
        `/api/dashboard/prices/symbol/${symbol}/history?timeframe=${apiTimeframe}&limit=${limit}`,
        { showLoading: false }
      ).toPromise();

      if (response?.data?.success && response?.data?.data?.candles) {
        const candles: HistoricalCandle[] = response.data.data.candles;

        // Convert to chart data format
        const formattedData: ChartData[] = candles
          .map((candle: HistoricalCandle) => {
            // The API returns "2026-01-21T15:25:00" without timezone
            // Database stores in IST (UTC+5:30), but JS Date treats it as local timezone
            // We need to explicitly parse as IST and convert to UTC timestamp

            // Parse as IST by appending +05:30 timezone
            const timestampWithTimezone = candle.timestamp.includes('+') || candle.timestamp.endsWith('Z')
              ? candle.timestamp
              : candle.timestamp + '+05:30';

            const date = new Date(timestampWithTimezone);
            const timestamp = Math.floor(date.getTime() / 1000);

            return {
              time: timestamp as Time,
              open: candle.open,
              high: candle.high,
              low: candle.low,
              close: candle.close,
              volume: candle.volume || 0,
            };
          })
          // Filter out candles with zero OHLC values (bad data)
          .filter(c => c.open > 0 && c.high > 0 && c.low > 0 && c.close > 0)
          .sort((a, b) => (a.time as number) - (b.time as number));

        setChartData(formattedData);
        historicalLoadedRef.current = true;

        // Update chart series - only if chart is still mounted
        if (isChartMountedRef.current && candleRef.current && volumeRef.current) {
          candleRef.current.setData(formattedData);
          volumeRef.current.setData(
            formattedData.map((d) => ({
              time: d.time,
              value: d.volume,
              color: d.close >= d.open ? "#26a69a80" : "#ef535080",
            }))
          );

          // Chart displays naturally with rightOffset showing ~5 days ahead
          // User can freely zoom and scroll - no x-axis control from code
        }
      }
    } catch (error) {
      console.error("Error fetching historical data:", error);
    } finally {
      if (isChartMountedRef.current) {
        setLoadingHistorical(false);
      }
    }
  }, [symbol, timeFrame]);

  /* -------------------- DATA LOAD -------------------- */
  useEffect(() => {
    if (!isChartMountedRef.current) return;

    // Check if timeframe, symbol, or wsEnabled actually changed
    const timeframeChanged = previousTimeframeRef.current !== timeFrame;
    const symbolChanged = previousSymbolRef.current !== symbol;
    const wsEnabledChanged = previousWsEnabledRef.current !== wsEnabled;
    const switchedToLive = !previousWsEnabledRef.current && wsEnabled;

    // Only clear data if timeframe or symbol changed
    // This prevents clearing history when just toggling live mode
    if (timeframeChanged || symbolChanged) {

      historicalLoadedRef.current = false;
      currentCandleRef.current = null;

      // Smoothly transition by setting data to empty first
      setChartData([]);

      // Small delay to ensure smooth transition
      const timer = setTimeout(() => {
        // Clear the chart series with smooth transition
        if (candleRef.current && volumeRef.current) {
          candleRef.current.setData([]);
          volumeRef.current.setData([]);
        }

        // Fetch historical data after clearing
        if (isChartMountedRef.current) {
          fetchHistoricalData();
        }
      }, 50);

      // Update refs to track current values
      previousTimeframeRef.current = timeFrame;
      previousSymbolRef.current = symbol;
      previousWsEnabledRef.current = wsEnabled;

      return () => clearTimeout(timer);
    } else if (switchedToLive) {
      // Going live: existing historical data is valid — live ticks will append/update the current candle.
      // Re-fetching here would briefly clear the chart and lose the current live candle.
      currentCandleRef.current = null;
      previousWsEnabledRef.current = wsEnabled;
    } else {
      // Just fetch data if data not loaded yet
      if (!historicalLoadedRef.current && isChartMountedRef.current) {
        fetchHistoricalData();
      }

      // Update ref to track current wsEnabled state
      previousWsEnabledRef.current = wsEnabled;
    }
  }, [timeFrame, symbol, wsEnabled, fetchHistoricalData]);

  /* -------------------- LOAD HISTORICAL ON MOUNT -------------------- */
  useEffect(() => {
    // Fetch historical data on mount if not already loaded
    if (symbol && !historicalLoadedRef.current && chartData.length === 0) {
      fetchHistoricalData();
    }
  }, [symbol, fetchHistoricalData, chartData.length]);

  // Keep chartDataRef in sync so the live-price effect can read it without triggering re-fires
  useEffect(() => { chartDataRef.current = chartData; }, [chartData]);

  /* -------------------- LIVE PRICE UPDATE -------------------- */
  useEffect(() => {
    if (!wsEnabled || !livePrice || livePrice === 0 || !candleRef.current || !isChartMountedRef.current) {
      currentCandleRef.current = null;
      return;
    }

    // Compute candle period using UTC+5:30 (IST) — timezone-agnostic, no browser locale needed
    const IST_OFFSET_S = 5 * 3600 + 30 * 60; // 19800 seconds
    const utcNowS = Math.floor(Date.now() / 1000);
    const istNowS = utcNowS + IST_OFFSET_S;

    const candleDurations: Record<TimeFrame, number> = {
      "1m": 60, "5m": 300, "15m": 900, "30m": 1800,
      "1h": 3600, "4h": 14400, "1D": 86400, "1W": 604800, "1M": 2592000,
    };
    const candleDuration = candleDurations[timeFrame];

    // Snap to candle boundary in IST, then convert back to UTC unix (matches stored candle timestamps)
    const istCandleStart = Math.floor(istNowS / candleDuration) * candleDuration;
    const candleStartTime = istCandleStart - IST_OFFSET_S;

    const data = chartDataRef.current;
    let shouldCreateNewCandle = true;

    if (data.length > 0) {
      const lastCandleTime = data[data.length - 1].time as number;
      if (candleStartTime < lastCandleTime) return;
      if (candleStartTime === lastCandleTime) shouldCreateNewCandle = false;
    }

    if (shouldCreateNewCandle && (!currentCandleRef.current || currentCandleRef.current.startTime !== candleStartTime)) {
      currentCandleRef.current = { time: candleStartTime as Time, open: livePrice, high: livePrice, low: livePrice, close: livePrice, startTime: candleStartTime };
      candleRef.current.update({ time: candleStartTime as Time, open: livePrice, high: livePrice, low: livePrice, close: livePrice });
      setChartData(prev => [...prev, { time: candleStartTime as Time, open: livePrice, high: livePrice, low: livePrice, close: livePrice, volume: 0 }]);
    } else {
      if (!currentCandleRef.current && data.length > 0) {
        const last = data[data.length - 1];
        currentCandleRef.current = { time: last.time, open: last.open, high: last.high, low: last.low, close: last.close, startTime: candleStartTime };
      }
      if (currentCandleRef.current) {
        currentCandleRef.current.high = Math.max(currentCandleRef.current.high, livePrice);
        currentCandleRef.current.low = Math.min(currentCandleRef.current.low, livePrice);
        currentCandleRef.current.close = livePrice;
        const upd = { time: currentCandleRef.current.time, open: currentCandleRef.current.open, high: currentCandleRef.current.high, low: currentCandleRef.current.low, close: currentCandleRef.current.close };
        candleRef.current.update(upd);
        // Only update chartData if OHLC actually changed (avoids infinite re-render loop)
        setChartData(prev => {
          if (!prev.length) return prev;
          const last = prev[prev.length - 1];
          if (last.high === upd.high && last.low === upd.low && last.close === upd.close) return prev;
          const updated = [...prev];
          updated[updated.length - 1] = { ...upd, volume: 0 };
          return updated;
        });
      }
    }

    setCurrentPrice(livePrice);
  }, [wsEnabled, livePrice, timeFrame]); // chartData intentionally excluded — read via chartDataRef to break the update loop

  /* -------------------- UPDATE PRICE STATS -------------------- */
  useEffect(() => {
    if (chartData.length > 1) {
      const last = chartData[chartData.length - 1];
      const prev = chartData[chartData.length - 2];
      if (!wsEnabled || livePrice === 0) {
        setCurrentPrice(last.close);
      }
      setPriceChange(last.close - prev.close);
      setPriceChangePercent(((last.close - prev.close) / prev.close) * 100);
    }

    updateIndicators(chartData);
  }, [chartData, indicators]);

  /* -------------------- SUPPORT/RESISTANCE LINES -------------------- */
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || !isChartMountedRef.current) return;

    // Remove old support lines
    supportLinesRef.current.forEach(line => {
      try {
        chart.removeSeries(line);
      } catch (e) {
        console.warn('Failed to remove support line:', e);
      }
    });
    supportLinesRef.current = [];

    // Remove old resistance lines
    resistanceLinesRef.current.forEach(line => {
      try {
        chart.removeSeries(line);
      } catch (e) {
        console.warn('Failed to remove resistance line:', e);
      }
    });
    resistanceLinesRef.current = [];

    // Only add lines if we have levels and chart data
    if ((supportLevels.length === 0 && resistanceLevels.length === 0) || chartData.length === 0) {
      return;
    }

    const firstTime = chartData[0].time;
    const lastTime = chartData[chartData.length - 1].time;

    // Add support lines
    supportLevels.forEach((level) => {
      const color = level.strength === 'Strong' ? '#26a69a' :
                    level.strength === 'Medium' ? '#4CAF50' : '#81C784';

      const line = chart.addSeries(LineSeries, {
        color: color,
        lineWidth: 2,
        lineStyle: LineStyle.Solid,
        priceLineVisible: true,
        lastValueVisible: true,
      });

      line.setData([
        { time: firstTime, value: level.level },
        { time: lastTime, value: level.level },
      ]);

      supportLinesRef.current.push(line);
    });

    // Add resistance lines
    resistanceLevels.forEach((level) => {
      const color = level.strength === 'Strong' ? '#ef5350' :
                    level.strength === 'Medium' ? '#FF5722' : '#FF8A80';

      const line = chart.addSeries(LineSeries, {
        color: color,
        lineWidth: 2,
        lineStyle: LineStyle.Solid,
        priceLineVisible: true,
        lastValueVisible: true,
      });

      line.setData([
        { time: firstTime, value: level.level },
        { time: lastTime, value: level.level },
      ]);

      resistanceLinesRef.current.push(line);
    });
  }, [supportLevels, resistanceLevels, chartData]);

  /* -------------------- OI-BASED LEVELS (Support/Resistance/MaxPain) -------------------- */
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || !isChartMountedRef.current) return;

    // Remove old OI lines
    oiLinesRef.current.forEach(line => {
      try { chart.removeSeries(line); } catch (e) { /* ignore */ }
    });
    oiLinesRef.current = [];

    if (oiLevels.length === 0 || chartData.length === 0) return;

    const firstTime = chartData[0].time;
    const lastTime = chartData[chartData.length - 1].time;

    oiLevels.forEach((lvl) => {
      const color = lvl.type === 'oi_support' ? '#00E676' :
                    lvl.type === 'oi_resistance' ? '#FF1744' :
                    '#FFD600'; // max_pain = yellow

      const lineStyle = lvl.type === 'max_pain' ? LineStyle.Dotted : LineStyle.Dashed;

      const line = chart.addSeries(LineSeries, {
        color: color,
        lineWidth: lvl.type === 'max_pain' ? 2 : 3,
        lineStyle: lineStyle,
        priceLineVisible: true,
        lastValueVisible: true,
        title: lvl.label,
      });

      line.setData([
        { time: firstTime, value: lvl.level },
        { time: lastTime, value: lvl.level },
      ]);

      oiLinesRef.current.push(line);
    });
  }, [oiLevels, chartData]);

  /* -------------------- VWAP OVERLAY -------------------- */
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || !isChartMountedRef.current) return;

    // Remove old VWAP
    if (vwapSeriesRef.current) {
      try { chart.removeSeries(vwapSeriesRef.current); } catch (e) { /* ignore */ }
      vwapSeriesRef.current = null;
    }

    if (!showVWAP || chartData.length < 2) return;

    const vwapData = calculateVWAP(chartData);
    if (vwapData.length === 0) return;

    const vwapSeries = chart.addSeries(LineSeries, {
      color: '#E040FB',
      lineWidth: 2,
      lineStyle: LineStyle.Solid,
      title: 'VWAP',
      priceLineVisible: false,
      lastValueVisible: true,
    });

    vwapSeries.setData(vwapData);
    vwapSeriesRef.current = vwapSeries;
  }, [chartData, showVWAP]);

  /* -------------------- PDH / PDL LINES -------------------- */
  useEffect(() => {
    const candle = candleRef.current;
    if (!candle || !isChartMountedRef.current) return;

    // Remove old PDH/PDL lines
    pdhlLinesRef.current.forEach(pl => {
      try { candle.removePriceLine(pl); } catch { /* ignore */ }
    });
    pdhlLinesRef.current = [];

    if (chartData.length < 2) return;

    // Find today's 9:15 AM IST as UTC unix seconds (timezone-agnostic)
    const IST_OFFSET_MS = 5.5 * 3600 * 1000;
    const istMidnightUtcMs = Math.floor((Date.now() + IST_OFFSET_MS) / 86400000) * 86400000 - IST_OFFSET_MS;
    const todayOpenTs = Math.floor(istMidnightUtcMs / 1000) + (9 * 3600 + 15 * 60); // 9:15 IST = 03:45 UTC

    // Find candles strictly before today's open — these are previous session candles
    const prevCandles = chartData.filter(c => (c.time as number) < todayOpenTs);
    if (prevCandles.length === 0) return;

    // Get previous day's candles (last trading day)
    const lastTs = prevCandles[prevCandles.length - 1].time as number;
    const prevDayStart = Math.floor(lastTs / 86400) * 86400; // start of that UTC day
    const prevDayCandles = prevCandles.filter(c => (c.time as number) >= prevDayStart);
    if (!prevDayCandles.length) return;

    const pdh = Math.max(...prevDayCandles.map(c => c.high));
    const pdl = Math.min(...prevDayCandles.map(c => c.low));

    pdhlLinesRef.current = [
      candle.createPriceLine({ price: pdh, color: '#ff9800', lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: `PDH ${pdh.toFixed(0)}` }),
      candle.createPriceLine({ price: pdl, color: '#4fc3f7', lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: `PDL ${pdl.toFixed(0)}` }),
    ];
  }, [chartData]);

  /* -------------------- SIGNAL + PATTERN MARKERS -------------------- */
  useEffect(() => {
    const api = markersApiRef.current;
    if (!api || !chartData.length) { api?.setMarkers([]); return; }

    const all: SeriesMarker<Time>[] = [];

    // CE/PE score-crossing markers (teal / red, size 1)
    for (const m of signalMarkers) {
      const targetTs = m.timestamp / 1000;
      const nearest = chartData.reduce((best, c) =>
        Math.abs((c.time as number) - targetTs) < Math.abs((best.time as number) - targetTs) ? c : best
      );
      all.push({
        time: nearest.time,
        position: m.type === 'CE' ? 'belowBar' : 'aboveBar',
        color: m.type === 'CE' ? '#26a69a' : '#ef5350',
        shape: m.type === 'CE' ? 'arrowUp' : 'arrowDown',
        text: m.type === 'CE' ? `▲CE +${m.score}` : `▼PE ${m.score}`,
        size: 1,
      } as SeriesMarker<Time>);
    }

    // Chart pattern markers (cyan bull / orange bear, size 2 — visually distinct)
    for (const p of detectedPatterns) {
      const candle = chartData.find(c => (c.time as number) === p.completionTime);
      if (!candle) continue;
      all.push({
        time: p.completionTime as Time,
        position: p.signal === 'bull' ? 'belowBar' : 'aboveBar',
        color: p.signal === 'bull' ? '#4fc3f7' : '#FF9800',
        shape: p.signal === 'bull' ? 'arrowUp' : 'arrowDown',
        text: `${PATTERN_LABEL[p.type]}${p.confirmed ? '✓' : '?'}`,
        size: 2,
      } as SeriesMarker<Time>);
    }

    all.sort((a, b) => (a.time as number) - (b.time as number));
    api.setMarkers(all);
  }, [signalMarkers, detectedPatterns, chartData]);

  /* -------------------- PATTERN DETECTION -------------------- */
  useEffect(() => {
    if (chartData.length < 20) return;

    // Run only when symbol or timeframe changes, not on every WS tick
    const key = `${symbol}:${timeFrame}`;
    if (patternsKeyRef.current === key) return;
    patternsKeyRef.current = key;

    const asPCandles = chartData as unknown as PatternCandle[];
    const patterns = detectChartPatterns(asPCandles, timeFrame);
    const quality = validateCandleQuality(asPCandles, timeframeToSecs(timeFrame));

    setDetectedPatterns(patterns);
    setCandleQuality(quality);
    onPatternsDetected?.(patterns);
  }, [chartData.length, timeFrame, symbol, onPatternsDetected]);

  // Reset patterns key when symbol/timeframe changes so detection re-runs after next data load
  useEffect(() => {
    patternsKeyRef.current = '';
    setDetectedPatterns([]);
    setCandleQuality(null);
    onPatternsDetected?.([]);
  }, [symbol, timeFrame]); // eslint-disable-line react-hooks/exhaustive-deps

  /* -------------------- RE-RUN PATTERN DETECTION EVERY 5 MIN WHILE LIVE -------------------- */
  // The effect above only fires on symbol/timeframe change, so patterns would otherwise
  // stay frozen from before "Go Live" even as new candles keep forming. Re-check on an
  // interval while streaming so patterns reflect the current live-built candle too.
  useEffect(() => {
    if (!wsEnabled) return;
    const interval = setInterval(() => {
      const data = chartDataRef.current;
      if (data.length < 20) return;
      const asPCandles = data as unknown as PatternCandle[];
      const patterns = detectChartPatterns(asPCandles, timeFrame);
      const quality = validateCandleQuality(asPCandles, timeframeToSecs(timeFrame));
      setDetectedPatterns(patterns);
      setCandleQuality(quality);
      onPatternsDetected?.(patterns);
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [wsEnabled, timeFrame, onPatternsDetected]);

  /* -------------------- VWAP CALCULATION -------------------- */
  const calculateVWAP = (data: ChartData[]): LineData[] => {
    if (data.length === 0) return [];

    // Group by trading day, then compute cumulative VWAP per day
    let cumulativeTPV = 0; // cumulative (typical price * volume)
    let cumulativeVolume = 0;
    let currentDay = -1;
    const result: LineData[] = [];

    for (const d of data) {
      const dayTs = Math.floor((d.time as number) / 86400);

      // Reset on new day
      if (dayTs !== currentDay) {
        cumulativeTPV = 0;
        cumulativeVolume = 0;
        currentDay = dayTs;
      }

      const typicalPrice = (d.high + d.low + d.close) / 3;
      const vol = d.volume || 1; // avoid division by zero
      cumulativeTPV += typicalPrice * vol;
      cumulativeVolume += vol;

      const vwap = cumulativeTPV / cumulativeVolume;
      result.push({ time: d.time, value: vwap });
    }

    return result;
  };

const updateIndicators = (data: any[]) => {
  const chart = chartRef.current;
  if (!chart) return;

  indicators.forEach((ind: any) => {
    let series = indicatorSeriesRef.current.get(ind.id);

    // REMOVE DISABLED INDICATOR
    if (!ind.enabled && series) {
      chart.removeSeries(series);
      indicatorSeriesRef.current.delete(ind.id);
      return;
    }

    // ADD NEW INDICATOR
    if (ind.enabled && !series) {
      series = chart.addSeries(LineSeries, {
        color: ind.color,
        lineWidth: 2,
      });

      indicatorSeriesRef.current.set(ind.id, series);
    }

    // UPDATE DATA
    if (series) {
      series.setData(
        ind.id === "ema9"
          ? calculateEMA(data, 9)
          : calculateSMA(data, ind.id === "sma50" ? 50 : 20)
      );
    }
  });
};


  /* -------------------- HELPERS -------------------- */
  const calculateSMA = (data: ChartData[], period: number): LineData[] =>
    data
      .map((_, i) =>
        i >= period - 1
          ? {
              time: data[i].time,
              value:
                data
                  .slice(i - period + 1, i + 1)
                  .reduce((a, b) => a + b.close, 0) / period,
            }
          : null
      )
      .filter(Boolean) as LineData[];

  const calculateEMA = (data: ChartData[], period: number): LineData[] => {
    const k = 2 / (period + 1);
    let ema = data[0].close;
    return data.map((d) => {
      ema = d.close * k + ema * (1 - k);
      return { time: d.time, value: ema };
    });
  };



  return (
    <div>
      <div style={{ position: 'relative' }}>
        {/* Timeframe Selector */}
      <div style={{
        position: 'absolute',
        top: '10px',
        right: '10px',
        zIndex: 1000,
        display: 'flex',
        gap: '5px',
        flexWrap: 'wrap'
      }}>
        {(['5m', '15m', '30m', '1h', '4h', '1D'] as TimeFrame[]).map((tf) => (
          <button
            key={tf}
            onClick={() => {
              setTimeFrame(tf);
              if (onTimeframeChange) onTimeframeChange(tf);
            }}
            style={{
              padding: '4px 8px',
              fontSize: '11px',
              border: 'none',
              borderRadius: '3px',
              cursor: 'pointer',
              backgroundColor: timeFrame === tf ? '#2962FF' : '#363c4e',
              color: '#fff',
              fontWeight: timeFrame === tf ? 'bold' : 'normal',
              transition: 'all 0.2s ease',
            }}
          >
            {tf}
          </button>
        ))}
      </div>

      {/* Live Status Indicator */}
      {wsEnabled && (
        <div style={{
          position: 'absolute',
          top: '10px',
          left: '10px',
          zIndex: 1000,
          padding: '4px 10px',
          fontSize: '11px',
          backgroundColor: '#26a69a',
          color: '#fff',
          borderRadius: '3px',
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          gap: '5px'
        }}>
          <span style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: '#fff',
            animation: 'pulse 1.5s infinite'
          }} />
          LIVE {livePrice > 0 && `• ${livePrice.toFixed(2)}`}
        </div>
      )}

      {/* Candle quality warning */}
      {candleQuality && !candleQuality.isReliable && !loadingHistorical && (
        <div style={{
          position: 'absolute', top: 10, left: 10, zIndex: 1000,
          padding: '3px 8px', fontSize: '10px', background: '#2a1a00',
          border: '1px solid #FF9800', color: '#FF9800', borderRadius: 3,
          display: 'flex', alignItems: 'center', gap: 5,
        }}
          title={candleQuality.issues.join(' · ')}
        >
          ⚠ Data: {candleQuality.issues.join(' · ')}
        </div>
      )}

      {/* Loading Indicator */}
      {loadingHistorical && (
        <div style={{
          position: 'absolute',
          top: '10px',
          left: '10px',
          zIndex: 1000,
          padding: '4px 10px',
          fontSize: '11px',
          backgroundColor: '#363c4e',
          color: '#fff',
          borderRadius: '3px',
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          gap: '5px'
        }}>
          <span style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: '#fff',
            animation: 'pulse 1.5s infinite'
          }} />
          Loading...
        </div>
      )}

      <div
        ref={chartContainerRef}
        className={`chart-container ${loadingHistorical ? 'loading' : ''}`}
        style={{ transition: 'opacity 0.3s ease' }}
      />

      {/* Crosshair Tooltip */}
      {tooltipData && (
        <div style={{
          position: 'absolute',
          left: tooltipPosition.x > 300 ? tooltipPosition.x - 200 : tooltipPosition.x + 20,
          top: tooltipPosition.y + 20,
          zIndex: 1000,
          backgroundColor: 'rgba(19, 23, 34, 0.95)',
          border: '1px solid #2a2e39',
          borderRadius: '4px',
          padding: '8px 12px',
          fontSize: '11px',
          color: '#d1d4dc',
          pointerEvents: 'none',
          minWidth: '180px',
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '6px', fontSize: '12px', color: '#fff' }}>
            {tooltipData.time}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 8px' }}>
            <span style={{ color: '#888' }}>Open:</span>
            <span style={{ fontWeight: '600' }}>{tooltipData.open.toFixed(2)}</span>

            <span style={{ color: '#888' }}>High:</span>
            <span style={{ fontWeight: '600', color: '#26a69a' }}>{tooltipData.high.toFixed(2)}</span>

            <span style={{ color: '#888' }}>Low:</span>
            <span style={{ fontWeight: '600', color: '#ef5350' }}>{tooltipData.low.toFixed(2)}</span>

            <span style={{ color: '#888' }}>Close:</span>
            <span style={{ fontWeight: '600' }}>{tooltipData.close.toFixed(2)}</span>

            <span style={{ color: '#888' }}>Volume:</span>
            <span style={{ fontWeight: '600' }}>{tooltipData.volume.toLocaleString()}</span>
          </div>

          {tooltipData.pattern && (() => {
            const p = tooltipData.pattern;
            const color = p.signal === 'bull' ? '#4fc3f7' : '#FF9800';
            return (
              <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #2a2e39' }}>
                <div style={{ fontWeight: 700, color, marginBottom: '3px' }}>
                  {PATTERN_FULL_NAME[p.type]} · {p.confidence}% · {p.confirmed ? 'Confirmed' : 'Watching'}
                </div>
                <div style={{ color: '#9e9e9e', maxWidth: '220px', whiteSpace: 'normal' }}>
                  {PATTERN_EXPLANATION[p.type]}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      </div>

      {/* Legend — explains every colored line/marker on the chart */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: '10px', padding: '4px 10px',
        fontSize: '10px', color: '#787b86', background: '#0d1117', borderTop: '1px solid #1e222d',
      }}>
        {showVWAP && <span><span style={{ color: '#E040FB' }}>●</span> VWAP</span>}
        <span><span style={{ color: '#ff9800' }}>●</span> Prev Day High</span>
        <span><span style={{ color: '#4fc3f7' }}>●</span> Prev Day Low</span>
        {supportLevels.length > 0 && <span><span style={{ color: '#26a69a' }}>●</span> Support</span>}
        {resistanceLevels.length > 0 && <span><span style={{ color: '#ef5350' }}>●</span> Resistance</span>}
        {oiLevels.length > 0 && (
          <>
            <span><span style={{ color: '#00E676' }}>●</span> OI Support</span>
            <span><span style={{ color: '#FF1744' }}>●</span> OI Resistance</span>
            <span><span style={{ color: '#FFD600' }}>●</span> Max Pain</span>
          </>
        )}
        <span><span style={{ color: '#4fc3f7' }}>▲</span> Bullish pattern</span>
        <span><span style={{ color: '#FF9800' }}>▼</span> Bearish pattern</span>
        <span style={{ color: '#555' }}>Hover a candle for details</span>
      </div>

    </div>
  );
};

export default AdvancedTradingChart;
